import React from 'react';
import { StepComponentProps, StepDefinition } from './StepDefinition';
import { Button, VStack, Text, Box } from '@chakra-ui/react';
import { RestaurantFacade } from 'pos/facades';
import { StepLoader } from './StepLoader';
import { Res } from 'core/api';
import { FormRef, useControlsFor } from 'core/ui-next/controls';
import { z } from 'zod';
import { ScrollableGrid } from 'pos/ui';
import { CompositionState } from './Composition';
import { useCompositionSetters } from './useCompositionSetters';
import { ProductButton } from '../../sale/ProductButton';

async function validate(_composition: CompositionState): Promise<boolean> {
	return true;
}

function clean(composition: CompositionState): CompositionState {
	return { ...composition, preparationNotes: '' };
}

const stateSchema = z.object({
	preparationNotes: z.string()
});

type State = z.infer<typeof stateSchema>;

const MAX_NOTES_LENGTH = 800;

export const Component = (props: StepComponentProps) => {

	const { composition, setComposition, buttonSize } = props;
	const { setPreparationNotes } = useCompositionSetters(setComposition);

	const { Form, TextArea } = useControlsFor<State>();
	const formRef = React.useRef<FormRef<State>>(null);

	const getData = React.useCallback(async (composition: CompositionState) => {
		if (composition.selectedProduct === null)
			throw new Error('Product has not been selected');

		const notes = await RestaurantFacade.getPredefinedNotes(composition.selectedProduct.SaleFormatId);
		return { notes };
	}, []);

	React.useEffect(() => {
		formRef.current?.setValue('preparationNotes', composition.preparationNotes);
	}, [composition.preparationNotes]);

	const addNote = async (note: string) => {
		const notesResult = await formRef.current?.getValue();
		if (notesResult?.ok) {
			const currentNotes = notesResult.value.preparationNotes ?? '';
			const newNote = currentNotes === ''
				? note
				: currentNotes + '\r\n' + note;

			setPreparationNotes(newNote.substring(0, MAX_NOTES_LENGTH));
		}
	};

	const clearNotes = () => {
		setPreparationNotes('');
	};

	// Esto es para configurar el grid de botones y que tengan dos filas visibles
	const gridMaxHeight = {
		height: {sm: '32', md: '46', lg: '53.5', 'phone-md': '32', 'phone-lg': '42'}
	};

	return <StepLoader {...props} getData={getData}>
		{({ notes }) => {

			const noteButtons = notes.map(note =>
				<ProductButton key={note.Text} value={note} size={buttonSize} onClick={async () => addNote(note.Text)} />
			);

			return <VStack width={'full'} height={'full'} gap={'6'}>
				<VStack width={'full'} gap={'4'} alignItems={'flex-start'}>
					<Text fontSize={'md'} fontWeight={'semibold'}>{Res.Msg.MSG_ADD_LINE_NOTE_COMPOSE()}</Text>
					<Box width={'full'} maxH={gridMaxHeight.height[buttonSize]} overflow={'auto'}>
						<ScrollableGrid items={noteButtons} buttonSize={buttonSize} columnGap={'2'} />
					</Box>
				</VStack>
				<VStack width={'full'} gap={'4'} alignItems={'flex-start'} flex={1}>
					<Box>
						<Text fontSize={'md'} fontWeight={'semibold'}>{Res.Msg.MSG_SELECTED_LINE_NOTE_COMPOSE()}</Text>
					</Box>
					<Box width={'full'} gap={'3'} display={'flex'} flexDirection={'column'} flex={1}>
						<Box width={'full'} display={'flex'} justifyContent={'flex-end'}>
							<Button
								fontSize={'xs'}
								fontWeight={'semibold'}
								paddingX={'2'}
								paddingY={'1'}
								onClick={clearNotes}
								variant={'outline'}
								height={'6'}
							>
								{Res.Msg.MSG_CLEAR_LINE_NOTE_COMPOSE()}
							</Button>
						</Box>
						<Form ref={formRef} initialValue={{ preparationNotes: composition.preparationNotes }} schema={stateSchema} style={{height: '100%'}}>
							<VStack width={'full'} height={'full'}>
								<TextArea
									linkTo={'preparationNotes'}
									bg={{ base: 'white', _dark: 'none' }}
									onWatch={setPreparationNotes}
									borderWidth={{ base: '0', _dark: '1px' }}
									resize={'none'}
									maxLength={MAX_NOTES_LENGTH}
									height={'full'}
								/>
							</VStack>
						</Form>
					</Box>
				</VStack>
			</VStack>;
		}}
	</StepLoader>;
};

const step: StepDefinition = {
	id: 'Notes',
	label: Res.Web.WIZARD_COMPOSE_SELECT_NOTES_LBL(),
	canFinish: false,
	useKeyPad: false,
	validate,
	clean,
	Component
};

export default step;
